import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Video, ArrowLeft, ExternalLink, Loader2, Clock, ShieldCheck,
  Maximize2, AlertCircle, CalendarCheck,
} from "lucide-react";

// Best-effort Jitsi domain + suffix. The backend creates `https://meet.jit.si/{room}#…`.
function parseRoom(meetingUrl) {
  if (!meetingUrl) return null;
  try {
    const u = new URL(meetingUrl);
    const room = u.pathname.replace(/^\//, "");
    return { domain: u.host, room };
  } catch { return null; }
}

export default function Meet() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [joined, setJoined] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    api.get(`/bookings/${id}`).then(r => setB(r.data)).catch(e => setErr(formatErr(e)));
  }, [id]);

  const room = useMemo(() => (b ? parseRoom(b.meeting_url) : null), [b]);

  const isOnline = b?.delivery_type === "online" && !!b?.meeting_url && !!room;

  // Live window: enable join 15 min before scheduled time, until 4 hours after.
  const now = Date.now();
  const scheduledMs = b?.date ? new Date(b.date).getTime() : null;
  const windowOpen = !scheduledMs || (scheduledMs - now <= 15 * 60 * 1000 && now - scheduledMs <= 4 * 3600 * 1000);
  const minsUntil = scheduledMs ? Math.round((scheduledMs - now) / 60000) : null;

  const fullJoinUrl = useMemo(() => {
    if (!room || !user) return "";
    const name = encodeURIComponent(user.name || "Hooman");
    const email = encodeURIComponent(user.email || "");
    // Skip Jitsi's prejoin/welcome to drop the user straight into the call.
    return `https://${room.domain}/${room.room}#userInfo.displayName="${name}"&userInfo.email="${email}"&config.prejoinPageEnabled=false&config.startWithVideoMuted=false&config.startWithAudioMuted=false`;
  }, [room, user]);

  const goFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) req.call(el);
  };

  if (err) return (
    <div className="max-w-xl mx-auto p-10 text-center" data-testid="meet-error">
      <AlertCircle size={28} className="mx-auto text-[#B84B4B]"/>
      <p className="text-sm text-[#B84B4B] mt-3">{err}</p>
      <Link to="/bookings" className="btn-secondary mt-5"><ArrowLeft size={14}/> Back to bookings</Link>
    </div>
  );

  if (!b) return <div className="max-w-3xl mx-auto p-10 text-[#8A8A8A]">Loading…</div>;

  if (!isOnline) return (
    <div className="max-w-xl mx-auto p-10 text-center" data-testid="meet-not-online">
      <Video size={28} className="mx-auto text-[#8A8A8A]"/>
      <h2 className="font-heading text-2xl mt-3">In-person booking</h2>
      <p className="text-sm text-[#4A4A4A] mt-2">This booking isn&apos;t an online consultation, so there&apos;s no video room for it.</p>
      <Link to="/bookings" className="btn-secondary mt-5"><ArrowLeft size={14}/> Back to bookings</Link>
    </div>
  );

  const other = b.counterpart || {};
  const dateNice = new Date(b.date).toLocaleString();

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8" data-testid="meet-page">
      <Link to="/bookings" className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 hover:text-[#0A0A0A]">
        <ArrowLeft size={14}/> Back to bookings
      </Link>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 mt-5">
        {/* Video stage */}
        <div className="card overflow-hidden" data-testid="meet-stage">
          {joined ? (
            <div className="relative bg-[#0A0A0A]" style={{ aspectRatio: "16/9" }}>
              <iframe
                ref={iframeRef}
                title="Hoomanz video room"
                src={fullJoinUrl}
                allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="meet-iframe"
              />
            </div>
          ) : (
            <div className="aspect-video bg-gradient-to-br from-[#0A0A0A] to-[#2A2A2A] flex flex-col items-center justify-center p-8 text-white">
              <Video size={36} className="opacity-80"/>
              <h1 className="font-heading text-3xl mt-3 text-center">{b.service_title}</h1>
              <p className="text-white/70 text-sm mt-1 inline-flex items-center gap-2">
                <CalendarCheck size={13}/> {dateNice}
              </p>

              {windowOpen ? (
                <button onClick={() => setJoined(true)}
                  className="mt-7 bg-[#0057FF] hover:bg-[#0044CC] text-white rounded-full px-7 py-3 inline-flex items-center gap-2 font-medium transition-colors"
                  data-testid="meet-join">
                  <Video size={16}/> Join the call
                </button>
              ) : (
                <div className="mt-7 bg-white/10 rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="meet-waiting">
                  <Clock size={14}/> {minsUntil > 0 ? `Starts in ${minsUntil} min` : "This session has ended"}
                </div>
              )}

              <p className="text-[11px] text-white/55 mt-5 inline-flex items-center gap-1">
                <ShieldCheck size={11}/> Powered by Jitsi · end-to-end encrypted · no app install needed
              </p>
            </div>
          )}

          {joined && (
            <div className="px-4 py-2.5 flex items-center justify-between text-xs text-[#4A4A4A] border-t border-[#E5E5E3]">
              <span className="inline-flex items-center gap-1"><Video size={12}/> Live · room <code className="font-mono">{room?.room.slice(0, 28)}…</code></span>
              <div className="flex items-center gap-2">
                <button onClick={goFullscreen} className="btn-ghost text-xs" data-testid="meet-fullscreen">
                  <Maximize2 size={12}/> Fullscreen
                </button>
                <a href={fullJoinUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="meet-open-new-tab">
                  <ExternalLink size={12}/> Open in new tab
                </a>
                <button onClick={() => { setJoined(false); nav("/bookings"); }} className="btn-ghost text-xs text-[#B84B4B]" data-testid="meet-leave">
                  Leave
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          <div className="card p-5" data-testid="meet-session-info">
            <p className="overline mb-2">Session</p>
            <p className="font-medium">{b.service_title}</p>
            <p className="text-sm text-[#4A4A4A] mt-1">{b.is_hooman_view ? "with" : "with"} <strong>{other.name || "your counterpart"}</strong></p>
            <p className="text-xs text-[#8A8A8A] mt-2 inline-flex items-center gap-1"><CalendarCheck size={12}/> {dateNice}</p>
          </div>

          <div className="card p-5">
            <p className="overline mb-3">Before you join</p>
            <ul className="space-y-2 text-sm text-[#4A4A4A]">
              <li className="flex gap-2"><span className="text-[#0057FF]">·</span> Test your camera and mic — your browser will ask for permission.</li>
              <li className="flex gap-2"><span className="text-[#0057FF]">·</span> Use Chrome, Edge, Firefox or Safari. Mobile works too.</li>
              <li className="flex gap-2"><span className="text-[#0057FF]">·</span> Find a quiet space with steady Wi-Fi.</li>
              <li className="flex gap-2"><span className="text-[#0057FF]">·</span> Be on time — the room locks automatically 4 hours after the scheduled start.</li>
            </ul>
          </div>

          {other.id && (
            <Link to={`/messages/${other.id}`} className="btn-secondary w-full justify-center" data-testid="meet-message">
              Message {other.name?.split(" ")[0] || "them"}
            </Link>
          )}

          <p className="text-[11px] text-[#8A8A8A] text-center">
            Having trouble joining? <a href={fullJoinUrl} target="_blank" rel="noreferrer" className="underline">Open the room directly</a>
          </p>
        </aside>
      </div>
    </div>
  );
}
